import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { interval, Subscription, forkJoin } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';

import { DialogDeleteNewComponent } from 'src/app/common/dialogs/dialog-delete-new/dialog-delete-new.component';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { PreviewRequestDialogComponent } from '../../real-time-dialogs/preview-request-dialog/preview-request-dialog.component';
import {
  ChangeReservedAgentDialogComponent,
  ChangeReservedAgentDialogResult
} from '../../real-time-dialogs/change-reserved-agent-dialog/change-reserved-agent-dialog.component';

import { realTimeService } from 'src/app/services/services/realtime.service';
import { SettingsService } from 'src/app/services/services/settings.service';

type MediaType = 'EMail' | number;

export interface ScheduledItem {
  requestId: number;
  description?: string;
  formattedNumberFrom?: string;
  caller?: string;

  formattedOrderedTime?: string;
  formattedScheduledTime?: string;

  formattedReservedFlag?: string;
  mediaType?: MediaType;

  allowDelete?: boolean;
}

@Component({
    selector: 'app-real-time-overview-sched',
    templateUrl: './real-time-overview-sched.component.html',
    styleUrls: ['./real-time-overview-sched.component.scss'],
    standalone: false
})
export class RealTimeOverviewSchedComponent implements OnInit, OnDestroy {
  /** Queue id from route */
  id: number | null = null;

  /** Header title */
  queueName = '—';

  /** Columns expected by your template */
  displayedColumns: string[] = [
    'caller',
    'description',
    'formattedOrderedTime',
    'formattedScheduledTime',
    'formattedReservedFlag',
    'preview'
    // 'select' is appended if propertyAllowDelete is true
  ];

  BreadcrumbsLink = [];

  /** Table + sorting */
  dataSource = new MatTableDataSource<ScheduledItem>([]);
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  /** UI state */
  isLoading = true;
  filter = '';

  /** Permissions / flags */
  propertyAllowDelete = true;

  /** Selection */
  selected: Record<number, boolean> = {};
  get anySelected(): boolean {
    return Object.values(this.selected).some(Boolean);
  }
  get allSelectableIds(): number[] {
    return (this.dataSource.data || []).filter(x => !!x.allowDelete).map(x => x.requestId);
  }
  get allSelected(): boolean {
    const ids = this.allSelectableIds;
    return ids.length > 0 && ids.every(id => !!this.selected[id]);
  }

  /** (Kept for template compatibility; we load all at once here) */
  curDisplayRows = 0;
  hasMore = false;

  /** Polling */
  pollingIntervalMs = 10000;
  private pollingSub?: Subscription;

  constructor(
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private i18n: I18NextPipe,
    private realTimeService: realTimeService,
    private settingsService: SettingsService
  ) {}

  async ngOnInit(): Promise<void> {
    this.id = Number(this.activatedRoute.snapshot.params.id ?? null);

    // Header name
    this.realTimeService
      .GetQueueName(this.id!)
      .pipe(take(1))
      .subscribe(name => {
        (this.queueName = name || '—'),
        this.BreadcrumbsLink = [
          { "label": "Home", "url": "/" },
          { "label": "Real-Time", "url": "/real-time/overview" },
          { "label": "Queue Overview", "url": "/real-time/overview" },
          { "label": name, "url": `/real-time/overview/sched/${this.id}` }];
      });

    // Sorting
    this.dataSource.sort = this.sort;

    // Client-side filter
    this.dataSource.filterPredicate = (row, filterText) => {
      const f = (filterText || '').trim().toLowerCase();
      if (!f) return true;
      return [
        row.caller,
        row.formattedNumberFrom,
        row.description,
        row.formattedOrderedTime,
        row.formattedScheduledTime,
        row.formattedReservedFlag
      ].some(v => (v || '').toLowerCase().includes(f));
    };

    // ⬇️ Pull realtime settings from SettingsService (/Settings/RealtimeSettings)
    const cfg: any = await this.settingsService.realtimeSettings();
    this.propertyAllowDelete = !!cfg?.propertyAllowDelete;
    this.pollingIntervalMs = this.normalizeInterval(cfg?.ajaxRefreshInterval);

    // Add select column if allowed (after settings loaded)
    if (this.propertyAllowDelete && !this.displayedColumns.includes('select')) {
      this.displayedColumns = ['select', ...this.displayedColumns];
    }

    // Initial load + polling
    this.loadAll(true);
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  /** === Fetch all scheduled items from the new endpoint === */
  private loadAll(showLoader = false): void {
    if (this.anySelected) return; // pause while selecting
    if (showLoader) this.isLoading = true;

    this.realTimeService
      .getScheduledCallList(this.id!)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(rows => {
        const mapped = (rows || []).map(this.mapApiToScheduled);
        this.dataSource.data = mapped;
        this.curDisplayRows = mapped.length;
        this.hasMore = false; // we load all in one call
        if (!this.anySelected) this.selected = {};
      });
  }

  /** Map /Realtime/GetScheduledCallList DTO → ScheduledItem expected by template */
  private mapApiToScheduled = (q: any): ScheduledItem => ({
    requestId: q.requestId ?? q.RequestId,
    description: q.description ?? q.Description ?? '‒',
    formattedNumberFrom: q.source ?? q.Source ?? '‒',
    caller: q.source ?? q.Source ?? '‒',

    formattedOrderedTime: q.formattedOrderedTime ?? q.FormattedOrderedTime ?? '',
    formattedScheduledTime: q.formattedScheduledTime ?? q.FormattedScheduledTime ?? '‒',

    // Prefer reserved user name; fall back to flag if necessary
    formattedReservedFlag:
      (q.reservedUserName ?? q.ReservedUserName) ??
      (q.reservedUserFlag != null ? String(q.reservedUserFlag) : '‒'),

    mediaType: q.mediaType ?? q.MediaType,

    // If backend doesn't provide allowDelete yet, enable while testing
    allowDelete: (q.allowDelete ?? q.AllowDelete) ?? true
  });

  /** Polling */
  private startPolling(): void {
    this.stopPolling();
    this.pollingSub = interval(this.pollingIntervalMs).subscribe(() => {
      if (!this.anySelected) this.loadAll(false);
    });
  }
  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  private normalizeInterval(raw: unknown): number {
    let ms = Number(raw) || 10000;          // default 10s
    if (ms > 0 && ms <= 60) ms *= 1000;     // treat small numbers as seconds
    return Math.max(1000, ms);              // clamp to >= 1s
  }

  /** Filter handlers */
  applyFilterFromProperty() {
    this.dataSource.filter = (this.filter || '').trim().toLowerCase();
  }
  clearFilter() {
    this.filter = '';
    this.dataSource.filter = '';
  }

  /** Selection */
  toggleSelectAll(checked: boolean) {
    const next = { ...this.selected };
    this.allSelectableIds.forEach(id => (next[id] = checked));
    this.selected = next;
    // Pause/resume polling like the old app
    if (this.anySelected) this.stopPolling();
    else this.startPolling();
  }
  toggleOne(id: number, checked: boolean) {
    this.selected = { ...this.selected, [id]: checked };
    if (this.anySelected) this.stopPolling();
    else this.startPolling();
  }

  /** Helpers */
  isEmailMedia(media: MediaType | undefined): boolean {
    return media === 'EMail' || media === 7;
  }

  /** Actions */
  onPreview(row: ScheduledItem, $event: Event) {
    $event.preventDefault();
    this.stopPolling();
    const dialogRef = this.dialog.open(PreviewRequestDialogComponent, {
      width: '720px',
      disableClose: false,
      data: { requestId: row.requestId }
    });
    dialogRef.afterClosed().subscribe(() => this.startPolling());
  }

  onDeleteSelected(): void {
    const ids = Object.keys(this.selected)
      .map(k => Number(k))
      .filter(k => this.selected[k]);

    if (!ids.length) return;

    const ref = this.dialog.open(DialogDeleteNewComponent, {
      disableClose: true,
      data: {
        title: this.i18n.transform('users:shared_deleteConfirm', { defaultValue: 'Delete confirmation' }),
        showTitleInHeader: true,
        additionalText: this.i18n.transform('realtime:queuedetails_deleteNote', { defaultValue: 'Are you sure…' }),
        confirmText: this.i18n.transform('Yes'),
        cancelText: this.i18n.transform('No')
      }
    });

    ref.afterClosed().subscribe(confirm => {
      if (!confirm) return;

      this.stopPolling();
      this.isLoading = true;

      this.realTimeService
        .RequestDelete(ids)
        .pipe(finalize(() => { this.isLoading = false; this.startPolling(); }))
        .subscribe(() => {
          // Remove locally then refresh for consistency
          const remaining = this.dataSource.data.filter(r => !ids.includes(r.requestId));
          this.dataSource.data = remaining;
          this.selected = {};
          this.curDisplayRows = remaining.length;
          this.loadAll(true);
        });
    });
  }

  get canEditReserved(): boolean {
    if (!this.anySelected) return false;
    const byId = new Map(this.dataSource.data.map(r => [r.requestId, r]));
    const selectedIds = Object.keys(this.selected).map(k => Number(k)).filter(id => this.selected[id]);
    // In legacy UI they allowed edit reserved for email media only
    return selectedIds.every(id => this.isEmailMedia(byId.get(id)?.mediaType));
  }

  onEditReserved(): void {
    const ids = Object.keys(this.selected)
      .map(k => Number(k))
      .filter(k => this.selected[k]);

    if (!ids.length) return;

    this.stopPolling();

    const dialogRef = this.dialog.open(ChangeReservedAgentDialogComponent, {
      width: '470px',
      disableClose: true,
      data: { requestIds: ids, queueId: this.id }
    });

    dialogRef.afterClosed().subscribe((result: ChangeReservedAgentDialogResult | undefined) => {
      if (!result || result.action === 'cancel') {
        this.startPolling();
        return;
      }

      // Apply per selected request (agentId/secondsFromNow depend on your dialog)
      const calls = ids.map(rid =>
        this.realTimeService.ReservedUserUpdate(
          rid,
          (result as any).secondsFromNow,
          (result as any).agentId
        )
      );

      this.isLoading = true;
      forkJoin(calls)
        .pipe(finalize(() => {
          this.isLoading = false;
          this.selected = {};
          this.startPolling();
        }))
        .subscribe(() => this.loadAll(true));
    });
  }

  /** "Get next" link exists in template; here we load all at once, so it's a no-op */
  onLoadMore(): void {
    // No paging from this endpoint; keep method for template safety.
    this.loadAll(false);
  }

  /** Help */
  openHelp(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      data: {
        title: this.i18n.transform('realtime:help_title_scheduledcallback', { defaultValue: 'Scheduled callbacks' }),
        text: this.i18n.transform('realtime:help_scheduledcallback', { defaultValue: '' }),
        renderAsHtml: true
      }
    });
  }
}
